/* eslint-disable max-lines */
import 'videojs-contrib-quality-levels';
import 'videojs-mux';
import '@hotmart/player-http-streaming';
import { IQualityLevel } from 'src/interfaces/Playlist';
import { IVideoJSPlayer, IOptionsBeforeRequest } from 'src/interfaces/VideoJS';
import { DEFAULT_RESOLUTION, DEFAULT_SOURCE } from 'src/components/ResolutionControl/constants';
import { EVENT_TYPES } from 'src/interfaces/enums';
import { formatTimeRanges } from 'src/utils/formatUtils';
import { isUrlFromCloudFront } from 'src/utils/urlUtils';
import DRMService from 'src/services/DRMService';
import ApplicationService from 'src/services/ApplicationService';
import { IPlayerError, CodeErrors } from 'src/interfaces/PlayerError';
import { v4 as uuidv4 } from 'uuid';
import { formatMedia } from '../../utils/playerUtils';
import { signCloudfrontUrl } from '../../utils/signatureUtils';
import { PATH_SUBTITLE } from '../shared/constants';
import { exitPictureInPicture, isApple, isPictureInPicture } from '../../utils/browserUtils';

class PlayerService {
  protected _playerInstance: IVideoJSPlayer | null = null;
  private _playerInitTime = 0;
  private _viewId = '';

  constructor(playerInstance: IVideoJSPlayer) {
    this._playerInitTime = Date.now();
    this._playerInstance = playerInstance;
    this._viewId = uuidv4();
  }

  hasPlayerInstance() {
    return !!this._playerInstance;
  }

  get playerInitTime() {
    return this._playerInitTime;
  }

  get viewId() {
    return this._viewId;
  }

  play() {
    this._playerInstance?.play()?.catch(() => {
      // do nothing
    });
  }

  pause() {
    this._playerInstance?.pause();
  }

  clean() {
    this._playerInstance?.dispose();
  }

  addPosterEventListener(eventTypes: EVENT_TYPES[], callback: (...args: unknown[]) => void) {
    eventTypes.map((event) => {
      this._playerInstance?.posterImage?.on(event.toLowerCase(), callback);
    });
  }

  addSubtitleListener(callback: () => void) {
    this.textTracks?.on(EVENT_TYPES.ADD_TRACK.toLowerCase(), callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListener(eventType: EVENT_TYPES, callback: (...args: any[]) => void) {
    this._playerInstance?.on(this._playerInstance?.el(), eventType.toLowerCase(), callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeEventListener(eventType: EVENT_TYPES, callback: (...args: any[]) => void) {
    this._playerInstance?.off(this._playerInstance?.el(), eventType.toLowerCase(), callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addFirstEventListener(eventType: EVENT_TYPES, callback: (...args: any[]) => void) {
    this._playerInstance?.one(this._playerInstance?.el(), eventType.toLowerCase(), callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListeners(eventTypes: EVENT_TYPES[], callback: (...args: any[]) => void) {
    eventTypes.forEach((eventType) => this.addEventListener(eventType, callback));
  }

  get userActive() {
    return this._playerInstance?.userActive() ?? false;
  }

  setUserActive(active: boolean) {
    this._playerInstance?.userActive(active);
  }

  get textTracks() {
    return this._playerInstance?.textTracks();
  }

  get watchTimes() {
    const timeRanges = this._playerInstance?.played();
    if (!timeRanges) return [];

    return formatTimeRanges(timeRanges);
  }

  get hasEnded() {
    return this._playerInstance?.ended();
  }

  get lastIntervalWatched() {
    const currentTime = this.currentTime;

    return this.watchTimes?.find((watchtime) => watchtime.end === currentTime);
  }

  get percentWatched() {
    if (!this._playerInstance) return 0;

    return (this._playerInstance.currentTime() * 100) / this._playerInstance.duration();
  }

  get bufferIndicatorPercent() {
    if (!this._playerInstance) return 0;

    const totalDuration = this._playerInstance.duration();
    const buffered = this._playerInstance.buffered();
    const currentTime = this._playerInstance.currentTime();

    for (let i = 0; i < buffered.length; i++) {
      const position = buffered.end(i);
      if (position > currentTime) return (position * 100) / totalDuration;
    }

    return 0;
  }

  get buffered() {
    return this._playerInstance?.buffered();
  }

  get playbackRate() {
    return this._playerInstance?.playbackRate();
  }

  get volumePercent() {
    return Math.round((this._playerInstance?.volume() || 0) * 100);
  }

  get currentTime() {
    return this._playerInstance?.currentTime() || 0;
  }

  get duration() {
    return this._playerInstance?.duration() || 0;
  }

  get vhs() {
    return this._playerInstance?.tech(true)?.vhs;
  }

  get tech() {
    return this._playerInstance?.tech(true);
  }

  get getVideoPlaybackQuality() {
    return this._playerInstance?.tech(true).getVideoPlaybackQuality();
  }

  get isAudio() {
    return this._playerInstance?.isAudio();
  }

  get playlists() {
    const playlists = this.qualityLevels;
    const sources = [DEFAULT_SOURCE];

    playlists?.forEach((quality: IQualityLevel) => {
      return sources.push(formatMedia(quality));
    });

    return sources;
  }

  get isMuted() {
    return this._playerInstance?.muted();
  }

  togglePlay() {
    this._playerInstance?.paused() ? this.play() : this.pause();
  }

  setCurrentTime(seconds: number) {
    this._playerInstance?.currentTime(seconds);
  }

  setCurrentTimePercent(percent: number) {
    if (percent < 0 || percent > 100) return;

    this._playerInstance?.currentTime((this._playerInstance?.duration() / 100) * percent);
  }

  setVolumePercent(percent: number) {
    if (this.isMuted && percent > 0) this._playerInstance?.muted(false);
    this._playerInstance?.volume(percent / 100);
  }

  unmuted() {
    this._playerInstance?.muted(false);
  }

  setPlaybackRate(rate: number) {
    this._playerInstance?.playbackRate(rate);
  }

  triggerEvent(eventType: EVENT_TYPES, ...args: unknown[]) {
    this._playerInstance?.trigger(eventType.toLowerCase(), ...args);
  }

  setResolution(currentResolution: string) {
    if (!this._playerInstance) return;

    this.qualityLevels.forEach((quality: IQualityLevel) => {
      quality.enabled = quality.height === parseInt(currentResolution) || currentResolution === DEFAULT_RESOLUTION;
    });

    this.triggerEvent(EVENT_TYPES.RESOLUTION_CHANGE);
  }

  get qualityLevels(): IQualityLevel[] {
    return this._playerInstance?.qualityLevels().levels_;
  }

  get currentPlaylist() {
    const qualityLevel = this.qualityLevels.find((quality: IQualityLevel) => quality.enabled);
    return qualityLevel && formatMedia(qualityLevel);
  }

  get sortPlaylist() {
    return this.playlists.sort((a, b) => parseInt(a.bandwidth) - parseInt(b.bandwidth));
  }

  activateSubtitle(active: boolean) {
    this.triggerEvent(EVENT_TYPES.SUBTITLE_CHANGE, active);
  }

  setupBeforeRequest() {
    const appData = ApplicationService?.applicationData;
    if (!this.vhs || !appData) return;

    this.vhs.xhr.beforeRequest = (options: IOptionsBeforeRequest) => {
      options.withCredentials = true;
      if (!isUrlFromCloudFront(options.uri)) options.withCredentials = false;
      else if (appData.flags?.ENABLE_PLAYER_SIGNED_URL && !appData.flags?.ENABLE_PLAYER_V4_API) {
        const { cookiesVideo, cookiesSubtitle } = appData.cloudFrontSignature;
        const cookie = options.uri.includes(PATH_SUBTITLE) ? cookiesSubtitle : cookiesVideo;
        options.uri = signCloudfrontUrl(options.uri, cookie);
      }

      return options;
    };
  }

  async enableDRM() {
    await import('videojs-contrib-eme');
    this._playerInstance?.eme();
  }

  initializeMediaKeys() {
    if (isApple()) return;
    this._playerInstance?.eme.initializeMediaKeys({}, DRMService.emeCallback, true);
  }

  async togglePictureInPicture() {
    try {
      if (isPictureInPicture()) {
        await exitPictureInPicture();
      } else {
        this._playerInstance?.requestPictureInPicture();
      }
    } catch (err) {
      console.error(err);
    }
  }

  startMutedPlay() {
    if (!this._playerInstance?.paused) return;

    this._playerInstance.muted(true);
    this.setVolumePercent(0);
    this._playerInstance.play();
  }

  autoPlay() {
    const promise = this._playerInstance?.play();

    if (!promise) {
      this.startMutedPlay();
    } else {
      promise
        .then(() => {
          // yeah, everything went fine
        })
        .catch(() => {
          this.startMutedPlay();
        });
    }
  }

  setFirstPlayTime(time: number) {
    const playPromise = this._playerInstance?.play();
    this._playerInstance?.currentTime(time);
    playPromise?.then(() => this._playerInstance?.pause());
  }

  disableLoader() {
    this._playerInstance?.hasStarted(true);
    this._playerInstance?.updateSource;
    this._playerInstance?.ready();
  }

  setCustomErrorMessages(errors: { [code: string]: IPlayerError }) {
    this._playerInstance?.errors.extend({ ...errors });
  }

  getErrorMessage() {
    const error = this._playerInstance?.error();
    const errorMessages = this._playerInstance?.errors.getAll();

    if (!error || !errorMessages) {
      return null;
    }

    if (!errorMessages[error.code]) {
      return errorMessages[CodeErrors.UNKNOWN] as IPlayerError;
    }

    const errorMessage = errorMessages[error.code] as IPlayerError;
    return errorMessage;
  }

  el() {
    return this._playerInstance?.el();
  }

  paused() {
    return this._playerInstance?.paused();
  }

  volume() {
    return this._playerInstance?.volume();
  }

  readyState() {
    return this._playerInstance?.readyState();
  }

  hasStarted() {
    return this._playerInstance?.hasStarted();
  }
}

export default PlayerService;
